import { Scope, Factory } from '../'
import {
  applyEntitySchema,
  objectSchema,
  openUnion,
  optionalTolerant,
  parseEntity,
  sourceJson,
} from '../../_utils/fcdc-v2'
import * as z from 'zod'

export enum RoleType {
  Global = 'Global',
  Guest = 'Guest',
  Customer = 'Customer',
  Presence = 'Presence',
  Site = 'Site',
  Location = 'Location',
}

const roleFields = {
  type: z.string(),
  scopes: optionalTolerant(z.number()),
  id: optionalTolerant(z.string()),
  siteId: optionalTolerant(z.string()),
}

const roleSchema = openUnion('type', {
  [RoleType.Global]: objectSchema({
    ...roleFields,
    type: z.literal(RoleType.Global),
  }),
  [RoleType.Customer]: objectSchema({
    ...roleFields,
    type: z.literal(RoleType.Customer),
  }),
  [RoleType.Presence]: objectSchema({
    ...roleFields,
    type: z.literal(RoleType.Presence),
  }),
  [RoleType.Site]: objectSchema({
    ...roleFields,
    type: z.literal(RoleType.Site),
  }),
  [RoleType.Location]: objectSchema({
    ...roleFields,
    type: z.literal(RoleType.Location),
  }),
})

export abstract class Role {
  type: RoleType
  scopes: Scope

  constructor(json: any, type: RoleType) {
    const source = sourceJson(json) as any
    this.type = type
    this.scopes = source.scopes as Scope
    applyEntitySchema(this, roleSchema, source, {})
  }

  static getFactory(): Factory<Role> {
    return new (class implements Factory<Role> {
      make(json: any): Role {
        switch (json?.type) {
          case RoleType.Global:
            return new GlobalRole(json)
          case RoleType.Customer:
            return new CustomerRole(json)
          case RoleType.Presence:
            return new PresenceRole(json)
          case RoleType.Site:
            return new SiteRole(json)
          case RoleType.Location:
            return new LocationRole(json)
          default:
            return parseEntity(roleSchema, json ?? {}) as Role
        }
      }

      getTableName(): string {
        throw new Error(
          'getTableName() should never be called on a Roles. Roles are not stored in IndexDB.'
        )
      }
    })()
  }
}

export class GlobalRole extends Role {
  constructor(json: any) {
    super(json, RoleType.Global)
  }
}

export class CustomerRole extends Role {
  id: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    super(source, RoleType.Customer)
    this.id = source.id
  }
}

export class PresenceRole extends Role {
  id: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    super(source, RoleType.Presence)
    this.id = source.id
  }
}

export class SiteRole extends Role {
  id: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    super(source, RoleType.Site)
    this.id = source.id
  }
}

export class LocationRole extends Role {
  id: string
  siteId: string
  constructor(json: any) {
    const source = sourceJson(json) as any
    super(source, RoleType.Location)
    this.id = source.id
    this.siteId = source.siteId
  }
}
